import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronsDown, Info } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ogai from "@/assets/image.png"
import { Sparkles } from "lucide-react";
import AnimatedCheckbox from "@/components/ui/AnimatedCheckbox";
import { GlassToggle } from "@/components/ui/GlassToggle";
import {
    PerplexityIcon,
    ClaudeIcon,
    GPTIcon,
    GeminiIcon,
    LlamaIcon,
    DeepseekIcon,
    QwenIcon,
    KimiIcon,
    GoogleAIIcon
} from "./icons";

interface Model {
    id: string;
    name: string;
    description: string;
    logoUrl: string;
    disabled?: boolean;
    disabledMessage?: string;
}

interface ModelSelectorProps {
    selectedModel: string;
    onModelChange: (model: string) => void;
    isDarkMode: boolean;
    // Multi-AI controls (optional for backward compatibility)
    isMultiAIMode?: boolean;
    selectedMultiAIModels?: string[];
    onToggleMultiAIMode?: (next: boolean) => void;
    onUpdateMultiAIModels?: (models: string[]) => void;
}

const models: Model[] = [
    {
        id: "lara",
        name: "LARA",
        description: "AI with auto execution",
        logoUrl: "/ai-svg-oreog.svg",
    },
    {
        id: "chat gpt",
        name: "GPT 5",
        description: "The Best Open AI model",
        logoUrl: "",
    },
    {
        id: "deepseek",
        name: "DeepSeek",
        description: "Advanced reasoning AI model",
        logoUrl: "",
    },
    {
        id: "claude",
        name: "Claude",
        description: "Most capable AI assistant",
        logoUrl: "",
        disabled: true,
        disabledMessage: "Only for Exclusive members",
    },
    {
        id: "gemini",
        name: "Gemini 3",
        description: "Google's most powerful AI model",
        logoUrl: "",
    },
    {
        id: "llama",
        name: "LLaMA",
        description: "Meta's open-source model",
        logoUrl: "",
    },
    {
        id: "qwen",
        name: "Qwen",
        description: "Qwen AI model for quick Reasoning",
        logoUrl: "",
    },
    {
        id: "sambanova",
        name: "Sambanova",
        description: "Fast AI model",
        logoUrl: ogai,
    },
    {
        id: "cerebras",
        name: "Cerebras",
        description: "Ultra-fast reasoning AI",
        logoUrl: ogai,
    },
    // {
    //   id: "perplexity",
    //   name: "Perplexity",
    //   description: "AI with live web search",
    //   logoUrl: ogai,
    // },
    {
        id: "google ai",
        name: "Google AI",
        description: "Google AI Mode",
        logoUrl: "/ai-svg-oreog.svg",
    },
    {
        id: "upstage",
        name: "Solar Pro",
        description: "Advanced reasoning AI with high-effort thinking",
        logoUrl: ogai,
    },
    {
        id: "kimi",
        name: "Kimi",
        description: "Kimi K2 ",
        logoUrl: "/svg/kimi.svg",
    },
    {
        id: "gpt5",
        name: "GPT-5.2",
        description: "latest Open AI Model (w/ rate limits)",
        logoUrl: "",
    },
    // {
    //   id: 'glm',
    //   name: 'Chat GPT',
    //   description: 'GPT-4 powered model',
    //   logoUrl: chatgpt
    //   },
    // // {
    // //   id: 'gpt',
    // //   name: 'ChatGPT',
    // //   description: 'GPT-4 powered assistant',
    // //   logoUrl:chatgpt
    // // },
];

export const ModelSelector = ({
    selectedModel,
    onModelChange,
    isDarkMode,
    isMultiAIMode = false,
    selectedMultiAIModels = [],
    onToggleMultiAIMode,
    onUpdateMultiAIModels,
    isImageMode,
}: ModelSelectorProps & { isImageMode?: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [exclusiveHover, setExclusiveHover] = useState<string | null>(null);
    const [exclusiveRect, setExclusiveRect] = useState<{ top: number; left: number; right: number; bottom: number; width: number; height: number }>({ top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 });
    const [exclusiveEl, setExclusiveEl] = useState<HTMLElement | null>(null);
    const exclusivePanelRef = useRef<HTMLDivElement | null>(null);
    const [exclusiveSticky, setExclusiveSticky] = useState(false);

    // Sync with one-shot multi-AI toggle to immediately reflect selected model
    useEffect(() => {
        const mapServiceToUI = (serviceId: string | null | undefined): string | null => {
            if (!serviceId) return null;
            const key = serviceId.toLowerCase();
            if (key === 'glm') return 'chat gpt';
            if (key === 'gpt5') return 'gpt5';
            if (key === 'upstage') return 'upstage';
            return key;
        };
        const handler = () => {
            try {
                const service = localStorage.getItem('chatWithOnlyNext');
                const ui = mapServiceToUI(service);
                if (ui) {
                    // Ensure we only switch to known models; fallback to ChatGPT
                    const exists = models.some((m) => m.id === ui && !m.disabled);
                    onModelChange(exists ? ui : 'chat gpt');
                }
            } catch { }
        };
        window.addEventListener('chatWithOnlyChanged', handler as EventListener);
        window.addEventListener('storage', handler as EventListener);
        // Initialize from current storage (in case toggle already set)
        handler();
        return () => {
            window.removeEventListener('chatWithOnlyChanged', handler as EventListener);
            window.removeEventListener('storage', handler as EventListener);
        };
    }, [onModelChange]);

    const selectedModelData =
        models.find((m) => m.id === selectedModel) || models[0];

    // Keyboard shortcut handler - Focus input (Ctrl+Shift+F). Do not intercept Ctrl+Shift+I.
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.shiftKey && (event.key === 'F' || event.key === 'f')) {
                event.preventDefault();
                const inputElement = document.querySelector(
                    'textarea[placeholder*="Ask"], input[placeholder*="Ask"]'
                ) as HTMLElement;
                inputElement?.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Keep the Claude info panel following the target while visible
    useEffect(() => {
        if (exclusiveHover !== 'claude' || !exclusiveEl) return;
        const update = () => {
            const r = exclusiveEl.getBoundingClientRect();
            setExclusiveRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [exclusiveHover, exclusiveEl]);

    // Close the Claude panel only on outside click/tap
    useEffect(() => {
        if (exclusiveHover !== 'claude') return;
        const handleDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            if (exclusivePanelRef.current && exclusivePanelRef.current.contains(target)) return;
            if (exclusiveEl && exclusiveEl.contains(target as Node)) return;
            setExclusiveHover(null);
            setExclusiveSticky(false);
        };
        document.addEventListener('mousedown', handleDown);
        document.addEventListener('touchstart', handleDown, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleDown);
            document.removeEventListener('touchstart', handleDown);
        };
    }, [exclusiveHover, exclusiveEl]);

    // Helper: normalize between selector ids and Multi-AI UI labels
    const toMultiUILabel = (id: string): string | null => {
        const key = (id || '').toLowerCase();
        if (key === 'chat gpt') return 'chatgpt';
        if (key === 'upstage') return 'solar pro';
        if (key === 'gpt5') return 'gpt 5';
        if (key === 'lara') return null; // exclude non-chat model from multi ai
        if (key === 'claude') return null; // disabled anyway
        return key; // deepseek, gemini, qwen, llama, sambanova, cerebras
    };

    const toggleMultiSelection = (modelId: string) => {
        if (!onUpdateMultiAIModels) return;
        const label = toMultiUILabel(modelId);
        if (!label) return;
        const exists = selectedMultiAIModels.some((x) => x.toLowerCase() === label);

        // Remove if already selected
        if (exists) {
            const next = selectedMultiAIModels.filter((x) => x.toLowerCase() !== label);
            onUpdateMultiAIModels(next);
            return;
        }

        // Build a base list respecting exclusivity between ChatGPT and GPT-5
        const isChatGPT = label === 'chatgpt';
        const isGPT5 = label === 'gpt 5';
        let base = selectedMultiAIModels.slice();

        if (isChatGPT) {
            base = base.filter((x) => x.toLowerCase() !== 'gpt 5');
        } else if (isGPT5) {
            base = base.filter((x) => x.toLowerCase() !== 'chatgpt');
        }

        // Enforce maximum of 6 selections
        const candidate = Array.from(new Set([...base, label]));
        if (candidate.length > 6) {
            // Do not update if it would exceed the cap
            return;
        }

        onUpdateMultiAIModels(candidate);
    };

    const selectedCount = (isMultiAIMode ? (selectedMultiAIModels?.length || 0) : 0);

    /* const getLogo = (m: Model) => {
      if (m.id === 'chat gpt' || m.id === 'gpt5') {
        // Per user mapping: dark mode uses light icon; light mode uses dark icon
        return isDarkMode ? chatgptLight : chatgptDark;
      }
      return m.logoUrl;
    }; */

    const renderModelIcon = (id: string, className: string) => {
        switch (id) {
            case 'perplexity':
                return <PerplexityIcon className={className} />;
            case 'kimi':
                return <KimiIcon className={className} />;
            case 'google ai':
                return <GoogleAIIcon className={className} />;
            case 'claude':
                return <ClaudeIcon className={className} />;
            case 'chat gpt':
            case 'gpt5':
                return <GPTIcon className={className} />;
            case 'gemini':
                return <GeminiIcon className={className} />;
            case 'llama':
                return <LlamaIcon className={className} />;
            case 'deepseek':
                return <DeepseekIcon className={className} />;
            case 'qwen':
                return <QwenIcon className={className} />;
            default:
                // Fallback for others like sambanova, cerebras, upstage, lara
                const model = models.find(m => m.id === id);
                return (
                    <img
                        src={model?.logoUrl || "/ai-svg-oreog.svg"}
                        alt={model?.name || id}
                        className={`${className} rounded-full object-cover select-none pointer-events-none`}
                    />
                );
        }
    };

    return (
        <div
            ref={dropdownRef}
            className={`relative transition-colors duration-150 rounded-lg`}
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 py-1.5 px-3 h-auto rounded-2xl transition-colors backdrop-blur-sm duration-150 ${isImageMode
                    ? isDarkMode
                        ? "bg-transparent hover:bg-white/5 text-white border border-white/10"
                        : "bg-transparent hover:bg-white/40 text-black border border-black/10"
                    : isDarkMode
                        ? "text-gray-300 hover:text-white bg-transparent hover:bg-[#383838]"
                        : "text-gray-700 hover:text-gray-900 bg-transparent hover:bg-[#f5f5f5]"
                    }`}
            >
                <div className="flex select-none items-center">
                    <div className="relative">
                        {renderModelIcon(selectedModelData.id, "w-7 h-7")}
                        {isMultiAIMode && selectedCount > 1 && (
                            <span className={`flex items-center justify-center absolute -bottom-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] leading-4 text-center font-semibold ${isDarkMode ? 'bg-sky-500 text-black border border-[#0b0b0b]' : 'bg-sky-600 text-white border border-white'}`}>{selectedCount}</span>
                        )}
                    </div>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 hidden xs:block select-none">
                        {selectedModelData.name}
                    </span>
                </div>
                <ChevronDown
                    className={`w-4 h-4 transition-transform duration-150 ${isOpen ? "rotate-180" : ""
                        }`}
                />
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-10 "
                        onClick={() => setIsOpen(false)}
                    />
                    <Card
                        className={`absolute rounded-2xl translate-y-1 top-full mt-2-auto right-0 w-64 z-20 p-2 border border-2 transition-all duration-150 ${isDarkMode
                            ? "bg-[#202020] backdrop-blur-xl border-[#303030]"
                            : "bg-white/95 backdrop-blur-xl border-gray-300/70"
                            }`}
                    >
                        <div className="relative">
                            <div className="relative">
                                {/* Multi-AI toggle */}
                                <div className={`flex items-center justify-between gap-3 px-2 pb-2 border-b ${isDarkMode ? 'border-[#2e2e2e]' : 'border-gray-200'}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium">Multi AI</span>
                                    </div>
                                    <div className="relative">
                                        <GlassToggle
                                            isActive={isMultiAIMode}
                                            isDarkMode={isDarkMode}
                                            label="Toggle Multi-AI Mode"
                                            className={isMultiAIMode ? 'glow-blue' : ''}
                                            onToggle={(value) => {
                                                if (onToggleMultiAIMode) {
                                                    onToggleMultiAIMode(value);
                                                    // When turning ON, default to exactly one selection mapped from current selectedModel
                                                    if (value && onUpdateMultiAIModels) {
                                                        const initial = toMultiUILabel(selectedModel);
                                                        onUpdateMultiAIModels(initial ? [initial] : []);
                                                    }
                                                    // When turning OFF, clear all multi selections
                                                    if (!value && onUpdateMultiAIModels) {
                                                        onUpdateMultiAIModels([]);
                                                    }
                                                }
                                            }}
                                        />

                                        <style>{`
                      .glass-toggle-wrapper.glow-blue .slider {
                        box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
                        position: relative;
                        background: linear-gradient(45deg, rgba(255, 103, 103, 0.5), rgba(145, 147, 255, 0.5));
                        
                      }
                      .glass-toggle-wrapper.glow-blue .slider::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        border-radius: 9999px;
                        padding: 2px;
                        background: linear-gradient(45deg, rgb(233, 134, 86), rgb(158, 113, 197), rgb(46, 153, 223));
                        
                        pointer-events: none;
                      }
                    `}</style>
                                    </div>
                                </div>

                                <div className="space-y-1 max-h-[400px] overflow-y-auto custom-scrollbar pb-2 pt-2">
                                    {models.map((model) => (
                                        <div
                                            key={model.id}
                                            className={`relative ${model.disabled ? "opacity-60" : ""}`}
                                        >
                                            <button
                                                onClick={(e) => {
                                                    if (model.disabled) return;
                                                    if (isMultiAIMode && onUpdateMultiAIModels) {
                                                        e.preventDefault();
                                                        toggleMultiSelection(model.id);
                                                        // keep dropdown open for multi selection
                                                        return;
                                                    }
                                                    onModelChange(model.id);
                                                    setIsOpen(false);
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (model.id === "claude") {
                                                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                        setExclusiveRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
                                                        setExclusiveEl(e.currentTarget as HTMLElement);
                                                        setExclusiveHover("claude");
                                                        setExclusiveSticky(false);
                                                    }
                                                }}
                                                onMouseLeave={() => {
                                                    if (!exclusiveSticky && exclusiveHover === 'claude') setExclusiveHover(null);
                                                }}
                                                onMouseDown={(e) => {
                                                    if (model.id === "claude") {
                                                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                        setExclusiveRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
                                                        setExclusiveEl(e.currentTarget as HTMLElement);
                                                        setExclusiveHover("claude");
                                                        setExclusiveSticky(true);
                                                    }
                                                }}
                                                onTouchStart={(e) => {
                                                    if (model.id === "claude") {
                                                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                        setExclusiveRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
                                                        setExclusiveEl(e.currentTarget as HTMLElement);
                                                        setExclusiveHover("claude");
                                                        setExclusiveSticky(true);
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    if (model.id === "claude") {
                                                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                        setExclusiveRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
                                                        setExclusiveEl(e.currentTarget as HTMLElement);
                                                        setExclusiveHover("claude");
                                                        setExclusiveSticky(true);
                                                    }
                                                }}
                                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-2xl transition-colors duration-150 text-left ${selectedModel === model.id && !model.disabled
                                                    ? isDarkMode
                                                        ? "bg-gray-700/50 text-white"
                                                        : "bg-gray-200 text-gray-900"
                                                    : isDarkMode
                                                        ? "hover:bg-[#2e2e2e] text-gray-300"
                                                        : "hover:bg-gray-100 text-gray-700"
                                                    } ${model.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                                            >
                                                {renderModelIcon(model.id, "w-10 h-10 flex-shrink-0")}
                                                <div className="flex-1 min-w-0 select-none">
                                                    <div className="font-medium text-sm select-none">{model.name}</div>
                                                    <div className={`text-xs select-none ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                                        {model.description}
                                                    </div>
                                                </div>
                                                {isMultiAIMode && !model.disabled && toMultiUILabel(model.id) && (
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <AnimatedCheckbox
                                                            checked={selectedMultiAIModels.some((x) => x.toLowerCase() === (toMultiUILabel(model.id) || ''))}
                                                            onChange={() => toggleMultiSelection(model.id)}
                                                            aria-label={`Select ${model.name}`}
                                                        />
                                                    </div>
                                                )}
                                                {!isMultiAIMode && selectedModel === model.id && !model.disabled && (
                                                    <div className={`w-2 h-2 rounded-full ${isDarkMode ? "bg-blue-400" : "bg-blue-500"}`} />
                                                )}
                                            </button>
                                        </div>
                                    ))}

                                    {/* 👇 Bottom section inside scroll */}
                                    <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 dark:border-gray-700 mt-2 pt-3">
                                        <div className="flex items-center justify-center gap-2 mb-2 select-none">
                                            <img
                                                src="/ai-svg-oreog.svg"
                                                alt="AI"
                                                className="w-4 h-4 opacity-90 select-none pointer-events-none"
                                            />
                                            <span className="font-medium select-none">
                                                More AI models coming soon
                                            </span>
                                        </div>
                                        {/* 👇 Skeleton replaced */}
                                        <div className="flex items-center gap-3 mt-2">
                                            <div
                                                className={`w-8 h-8 rounded-full ${isDarkMode
                                                    ? "bg-gradient-to-r from-gray-700 to-gray-600"
                                                    : "bg-gradient-to-r from-gray-200 to-gray-300"
                                                    }`}
                                            />
                                            <div
                                                className={`h-4 w-32 rounded ${isDarkMode
                                                    ? "bg-gradient-to-r from-gray-700 to-gray-600"
                                                    : "bg-gradient-to-r from-gray-200 to-gray-300"
                                                    }`}
                                            />

                                        </div>
                                    </div>
                                </div>

                                {/* 👇 Gradient + scroll-down chevron overlay */}
                                <div
                                    className={`absolute bottom-0 left-0 right-0 h-8 flex items-center justify-center pointer-events-none ${isDarkMode
                                        ? "bg-gradient-to-t from-[#202020] to-transparent"
                                        : "bg-gradient-to-t from-white to-transparent"
                                        }`}
                                >
                                    <ChevronsDown className="w-4 h-4 text-gray-400 animate-bounce" />
                                </div>
                            </div>
                        </div>
                    </Card>
                    {exclusiveHover === "claude" && createPortal(
                        (() => {
                            const gap = 8;
                            const panelW = 180; // approx for positioning
                            const panelH = 56;  // approx height
                            const placeTop = exclusiveRect.bottom + panelH + gap > window.innerHeight;
                            const top = placeTop ? Math.max(8, exclusiveRect.top - panelH - gap) : Math.min(window.innerHeight - gap, exclusiveRect.bottom + gap);
                            const left = Math.min(window.innerWidth - gap - panelW, Math.max(20, exclusiveRect.right - panelW));
                            return (
                                <div
                                    ref={exclusivePanelRef}
                                    className={`fixed z-[1000000] rounded-2xl p-2 border w-[max-content] gap-2 flex flex-row bg-white border-gray-200 text-gray-800 shadow-lg`}
                                    style={{ top, left, animation: 'fadeInUp 0.2s ease-out both' }}
                                // Keep open; no auto-hide on leave
                                // Add a light entrance animation
                                >
                                    <div className="text-sm font-medium text-center">Only for Exclusive members</div>
                                </div>
                            );
                        })(),
                        document.body
                    )}
                </>
            )}
        </div>
    );
};
